import {
  createSlice,
  createAsyncThunk,
  createSelector } from
"@reduxjs/toolkit";
import { ApolloClient, gql, NormalizedCacheObject } from "@apollo/client";
import { RootState } from "@src/redux/store";
import * as TYPES from "@src/redux/types";

const GRAPHQL = gql`
  query getClientOrganization($filter: organizationFilterInput) {
    getOrganization(filter: $filter) {
      id
      name
      shortName
      usernamePrefix
      type
      levelId
      parentOrganizationId
      email
      mobile
      logoUrl
      hasNCD
      domainName
      shouldForwardToOrganizationServer
      organizationServerUrl
      organizationServerToken
      subjectLabel
      allowedReadingSyncTime
      sessionMinutes
      timezoneOffset
      malePopulation
      femalePopulation
      thirdPopulation
      updateReferenceId
      hasPopulationCount
      hasCovidScore
      hasSubjectLogin
      allowReadingManualEntry
      hasAdditionalReports
      additionalReportNames
      maximumReportUploads
      rootLevelList {
        levelAttributeList {
          id
          name
          sortOrder
          isMandatory
          fieldType
          optionList
        }
        allowKitAssign
        allowOperatorLogin
        orderNumber
        parentLevelId
        name
        id
      }
      subjectKeyList {
        isMandatory
        priorityOrder
        name
        id
      }
      customTestGroupList {
        customTestList {
          id
          name
          shortQuestion
          type
          optionList
          orderNumber
        }
        orderNumber
        name
        organizationId
        id
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.OrganizationState = {
  result: null,
  error: null,
  loading: false
};

export const getClientOrganizationRequest = createAsyncThunk<
  TYPES.Organization,
  {filter?: TYPES.OrganizationFilterInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  "organization/get_client_organization",
  async (
  params: {filter?: TYPES.OrganizationFilterInput;},
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params,
        fetchPolicy: "network-only"
      });
      return response.data.getOrganization;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || "An error occurred",
        statusCode: err.statusCode || "500"
      });
    }
  }
);

const getClientOrganizationSlice = createSlice({
  name: "get_client_organization",
  initialState: initialState,
  reducers: {
    getClientOrganizationClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getClientOrganizationRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getClientOrganizationRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getClientOrganizationRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: "Failed to fetch",
        statusCode: "500"
      };
      state.loading = false;
    });
  }
});

export const { getClientOrganizationClear } =
getClientOrganizationSlice.actions;

export const getClientOrganization = createSelector(
  (state: RootState) => state.organization.getClientOrganization.result,
  (state: RootState) => state.organization.getClientOrganization.error,
  (state: RootState) => state.organization.getClientOrganization.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getClientOrganizationSlice.reducer;